import React, { useState } from 'react';
import { useOperatorAuth } from '../context/OperatorAuthContext';
import { ApiError } from '../api/client';

const STATUS_COPY: Record<string, string> = {
  sent: 'A new link is on its way. It works once and expires in 24 hours.',
  already_verified: 'This address is already verified.',
  mail_off: 'This server is not sending mail right now, so no link was sent. Ask the platform operator.',
};

/**
 * Button that asks the server to mail a fresh verification link to the
 * signed-in operator (POST /operators/email/verify/request). The server's
 * answer is honest about state, so each status gets its own sentence.
 */
const ResendVerification: React.FC<{ label?: string; onVerified?: () => void }> = ({
  label = 'Send verification link',
  onVerified,
}) => {
  const { api } = useOperatorAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    setSending(true);
    setResult(null);
    setError(null);
    try {
      const res = await api.requestEmailVerification();
      setResult(STATUS_COPY[res.status] ?? `Server answered: ${res.status}`);
      if (res.status === 'already_verified') onVerified?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not ask for a new link.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="resend-verification">
      <button type="button" className="secondary-button" onClick={onClick} disabled={sending}>
        {sending ? 'Sending…' : label}
      </button>
      {result && <p className="auth-notice" role="status">{result}</p>}
      {error && <div className="auth-error" role="alert">{error}</div>}
    </div>
  );
};

export default ResendVerification;
